import { useState, useCallback } from "react";
import { generateProcessCode } from "@/apis/supplier";
import { message } from "antd";

// ==================== 常量配置 ====================
const FLOW_CODE_ENUM = "ZHUN_RU_PING_GU";

// ==================== 类型定义 ====================
/**
 * useProcessCode Hook 返回值类型
 */
export interface UseProcessCodeReturn {
  /**
   * 流程编号
   */
  processCode: string;
  /**
   * 加载状态
   */
  loading: boolean;
  /**
   * 错误信息
   */
  error: string | null;
  /**
   * 是否成功获取
   */
  isSuccess: boolean;
  /**
   * 获取流程编号
   */
  fetchProcessCode: () => Promise<void>;
  /**
   * 重试获取
   */
  retry: () => Promise<void>;
  /**
   * 重置状态
   */
  reset: () => void;
}

// ==================== 自定义Hook ====================
/**
 * 获取流程单号的Hook
 * 在弹框打开时调取接口获取单号，用于企业微信二维码生成
 * @param autoFetch 是否自动获取（默认不自动获取，需主动调用）
 * @returns Hook返回值
 */
export const useProcessCode = (
  autoFetch: boolean = false,
): UseProcessCodeReturn => {
  const [processCode, setProcessCode] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // ==================== 最大重试次数 ====================
  const MAX_RETRY = 3;

  // ==================== 获取流程单号 ====================
  /**
   * 获取流程单号的内部实现
   */
  const fetchProcessCodeInternal = useCallback(
    async (isRetry: boolean = false) => {
      try {
        setLoading(true);
        setError(null);

        const response = await generateProcessCode(FLOW_CODE_ENUM);

        // 获取单号，根据接口实际返回结构调整
        const code =
          response.data?.data ||
          response.data?.processCode ||
          response.data?.code ||
          "";

        if (!code) {
          throw new Error("后端返回的流程编号为空");
        }

        setProcessCode(code);
        setIsSuccess(true);
        setRetryCount(0);

        // 成功获取后的提示
        if (!isRetry) {
        }
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : "获取流程编号失败";
        setError(errorMsg);
        setIsSuccess(false);

        // 如果是重试，显示错误提示
        if (isRetry && retryCount >= MAX_RETRY) {
          message.error(`获取流程编号失败，已重试${MAX_RETRY}次，请稍后重试`);
        }
      } finally {
        setLoading(false);
      }
    },
    [retryCount],
  );

  // ==================== 获取流程单号（外部调用） ====================
  const fetchProcessCode = useCallback(async () => {
    setRetryCount(0);
    await fetchProcessCodeInternal(false);
  }, [fetchProcessCodeInternal]);

  // ==================== 重试获取 ====================
  const retry = useCallback(async () => {
    if (retryCount >= MAX_RETRY) {
      message.error(`已达到最大重试次数(${MAX_RETRY})，请稍后重试`);
      return;
    }

    setRetryCount((prev) => prev + 1);
    await fetchProcessCodeInternal(true);
  }, [retryCount, fetchProcessCodeInternal]);

  // ==================== 重置状态 ====================
  const reset = useCallback(() => {
    setProcessCode("");
    setLoading(false);
    setError(null);
    setIsSuccess(false);
    setRetryCount(0);
  }, []);

  // ==================== 自动获取 ====================
  if (autoFetch && !processCode && !loading) {
    // 使用 Effect 处理自动获取（通过外部调用而不是在 Hook 内部）
    // 这样可以避免无限循环
  }

  return {
    processCode,
    loading,
    error,
    isSuccess,
    fetchProcessCode,
    retry,
    reset,
  };
};
